import React from "react";
import { ExternalLink, Zap, Dumbbell, Target, Gamepad2, BarChart3, CheckCircle, Users, Globe, Brain, Timer, Mail } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const EliteTrialPrep = () => {
  const developAreas = [
    {
      title: "Sprint Training",
      icon: <Zap className="w-8 h-8 text-cyan-400" />,
      items: [
        "Acceleration & Technique",
        "5m, 10m, 20m Sprints",
        "Change of Direction",
        "Reactive & Flying Sprints",
        "Speed Testing & Progress"
      ]
    },
    {
      title: "Strength & Conditioning",
      icon: <Dumbbell className="w-8 h-8 text-cyan-400" />,
      items: [
        "Lower & Upper Body Strength",
        "Power & Explosiveness",
        "Core Stability",
        "Injury Prevention",
        "Mobility & Flexibility"
      ]
    },
    {
      title: "Technical Under Pressure",
      icon: <Target className="w-8 h-8 text-cyan-400" />,
      items: [
        "First Touch & Receiving",
        "Passing & Combinations",
        "Dribbling & Ball Mastery",
        "Finishing & Shooting",
        "Skills Under Fatigue"
      ]
    },
    {
      title: "Trial-Specific Game Scenarios",
      icon: <Gamepad2 className="w-8 h-8 text-cyan-400" />,
      items: [
        "1v1, 2v2, 3v3 Situations",
        "Transition Play",
        "Decision Making",
        "Communication",
        "High Intensity Games"
      ]
    }
  ];

  const outcomes = [
    { icon: <Zap className="w-6 h-6" />, title: "Faster Acceleration", desc: "Explosive first steps that beat opponents." },
    { icon: <Dumbbell className="w-6 h-6" />, title: "Stronger Body", desc: "Build strength, power & resilience." },
    { icon: <Target className="w-6 h-6" />, title: "Sharper Skills", desc: "Perform skills under pressure." },
    { icon: <Brain className="w-6 h-6" />, title: "Stronger Mindset", desc: "Confidence, focus & mentality." },
    { icon: <CheckCircle className="w-6 h-6" />, title: "Trial Ready", desc: "Be ready when opportunity calls." },
    { icon: <Globe className="w-6 h-6" />, title: "Global Opportunities", desc: "Pathway to NPL, UK & Overseas." },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-950 via-black to-gray-950"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-cyan-400 font-semibold tracking-widest uppercase mb-4">Football X Academy</p>
            <h1 className="text-5xl md:text-7xl font-black mb-4">
              ELITE
            </h1>
            <h2 className="text-4xl md:text-6xl font-black text-cyan-400 mb-6">
              TRIAL PREP
            </h2>
            <p className="text-2xl font-bold text-gray-300 mb-2 italic">
              Train. Perform. <span className="text-cyan-400">Get Scouted.</span>
            </p>
            <div className="flex flex-wrap justify-center gap-4 mt-8 mb-10">
              <span className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-400 px-4 py-2 rounded-full font-semibold text-sm">8 WEEK PROGRAM</span>
              <span className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-400 px-4 py-2 rounded-full font-semibold text-sm">SMALL GROUP</span>
              <span className="bg-cyan-500/20 border border-cyan-400/40 text-cyan-400 px-4 py-2 rounded-full font-semibold text-sm">ONLY 4 PLAYERS PER GROUP</span>
            </div>
            <a
              href="https://form.jotform.com/261598653182063"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-black font-bold rounded-lg text-lg transition-colors duration-200"
              data-testid="elite-trial-book-spot"
            >
              Book Your Spot Now
            </a>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-gray-900 to-gray-950 border border-gray-800 rounded-2xl p-8 md:p-12">
            <p className="text-lg text-gray-300 leading-relaxed mb-8">
              A high performance program designed to prepare players for <span className="text-cyan-400 font-semibold">NPL, A-League, UK & Overseas</span> trials.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: "Only 4 Players", sub: "Per Group" },
                { label: "High Coach", sub: "To Player Ratio" },
                { label: "Individual Feedback", sub: "Every Session" },
                { label: "Designed For", sub: "NPL, Academy & Overseas Trials" },
              ].map((item, i) => (
                <div key={i} className="text-center bg-black/50 rounded-xl p-4 border border-gray-800">
                  <p className="text-lg font-bold text-white">{item.label}</p>
                  <p className="text-sm text-cyan-400">{item.sub}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap justify-center gap-6 mt-8">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-semibold">Trial Focused</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-semibold">Performance Driven</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-cyan-400" />
                <span className="text-sm font-semibold">Results Proven</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Develop */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold">
              WHAT WE <span className="text-cyan-400">DEVELOP</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {developAreas.map((area, index) => (
              <Card key={index} className="bg-gray-900 border-gray-800 hover:border-cyan-400 transition-colors" data-testid={`develop-${index}`}>
                <CardContent className="p-6">
                  <div className="mb-4">{area.icon}</div>
                  <h3 className="text-lg font-bold text-white mb-4">{area.title}</h3>
                  <ul className="space-y-2">
                    {area.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                        <span className="text-cyan-400 mt-0.5">-</span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* GPS Performance Tracking */}
      <section className="py-16 bg-gray-950">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-400/30 rounded-2xl p-8">
              <BarChart3 className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold mb-2">GPS PERFORMANCE TRACKING</h3>
              <p className="text-cyan-400 font-semibold mb-2">We Track. We Improve. We Perform.</p>
              <p className="text-gray-400">
                Monthly reports for players & parents covering top speed, sprint distance, hard running, 
                work rate, player load and heat maps.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold">YOUR PATHWAY <span className="text-cyan-400">TO THE NEXT LEVEL</span></h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 max-w-5xl mx-auto">
            {outcomes.map((outcome, i) => (
              <div key={i} className="text-center p-4 bg-gray-900 rounded-xl border border-gray-800 hover:border-cyan-400 transition-colors">
                <div className="text-cyan-400 flex justify-center mb-3">{outcome.icon}</div>
                <h4 className="text-sm font-bold mb-1">{outcome.title}</h4>
                <p className="text-xs text-gray-500">{outcome.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-cyan-500">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-black mb-2">
            LIMITED SPOTS. MAXIMUM ATTENTION.
          </h2>
          <p className="text-xl font-bold text-black mb-6">
            ONLY 4 PLAYERS PER GROUP!
          </p>
          <a
            href="https://form.jotform.com/261598653182063"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-8 py-4 bg-black hover:bg-gray-800 text-white font-bold rounded-lg text-lg transition-colors duration-200"
            data-testid="elite-trial-book-bottom"
          >
            Book Your Spot Now
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EliteTrialPrep;
